// 首页展示配置模块服务层

import { homeDisplayApi } from "../api";
import {
  HomeDisplayConfig,
  DisplayItem,
  DisplayPosition,
  GroupedDisplayItems,
  PopupTiming,
  TimingType,
  GroupedPopupTimings,
  PopupQueueItem,
  PopupCategory,
  PopupPriority,
  SourceType,
  ActivityType,
} from "../types";
import { useHomeDisplayStore } from "../stores";

/**
 * 首页展示配置服务类
 */
export class HomeDisplayService {
  private static instance: HomeDisplayService;
  // 请求去重：存储当前正在进行的请求 Promise
  private pendingRequest: Promise<HomeDisplayConfig> | null = null;
  // 最后一次成功加载的时间戳
  private lastLoadTime = 0;
  // 最小加载间隔（毫秒）
  private static readonly MIN_LOAD_INTERVAL = 2000;

  static getInstance(): HomeDisplayService {
    if (!HomeDisplayService.instance) {
      HomeDisplayService.instance = new HomeDisplayService();
    }
    return HomeDisplayService.instance;
  }

  /**
   * 加载首页展示配置（带去重和节流）
   */
  async loadConfig(): Promise<HomeDisplayConfig> {
    const now = Date.now();
    const { data } = useHomeDisplayStore.getState();

    // 节流：如果距离上次加载时间小于最小间隔，且已有数据，直接返回缓存数据
    if (
      now - this.lastLoadTime < HomeDisplayService.MIN_LOAD_INTERVAL &&
      data
    ) {
      return data;
    }

    // 去重：如果已有正在进行的请求，直接返回该请求
    if (this.pendingRequest) {
      return this.pendingRequest;
    }

    // 创建新请求
    this.pendingRequest = this._doLoadConfig();

    try {
      const result = await this.pendingRequest;
      this.lastLoadTime = Date.now();
      return result;
    } finally {
      this.pendingRequest = null;
    }
  }

  /**
   * 实际执行加载配置的内部方法
   */
  private async _doLoadConfig(): Promise<HomeDisplayConfig> {
    const { setLoading, setData } = useHomeDisplayStore.getState();

    try {
      setLoading(true);

      // 直接请求接口，不使用缓存
      const data = await homeDisplayApi.getConfig();

      setData(data);
      return data;
    } catch (error) {
      console.error("首页展示配置加载失败:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  /**
   * 强制重新拉取 config（绕过节流与缓存），用于「次数过多」弹窗关闭后拿到最新冷却状态
   */
  async refreshConfig(): Promise<HomeDisplayConfig> {
    return this._doLoadConfig();
  }

  /**
   * 按位置分组 Display Items
   * @param items DisplayItem数组
   * @returns 按位置分组的对象
   */
  groupDisplayItemsByPosition(items: DisplayItem[]): GroupedDisplayItems {
    const grouped: GroupedDisplayItems = {};

    items.forEach(item => {
      if (!grouped[item.position]) {
        grouped[item.position] = [];
      }
      grouped[item.position]!.push(item);
    });

    // 每个位置内按sort排序
    Object.values(DisplayPosition).forEach(position => {
      if (grouped[position]) {
        grouped[position]!.sort((a, b) => a.sort - b.sort);
      }
    });

    return grouped;
  }

  /**
   * 获取指定位置的 Display Items
   * @param items DisplayItem数组
   * @param position 位置枚举
   * @returns 排序后的DisplayItem数组
   */
  getDisplayItemsByPosition(
    items: DisplayItem[],
    position: DisplayPosition
  ): DisplayItem[] {
    return items
      .filter(item => item.position === position)
      .sort((a, b) => a.sort - b.sort);
  }

  /**
   * 按时机分组 Popup Timings
   * @param timings PopupTiming数组
   * @returns 按时机分组的对象
   */
  groupPopupTimingsByType(timings: PopupTiming[]): GroupedPopupTimings {
    const grouped: GroupedPopupTimings = {};

    timings.forEach(timing => {
      grouped[timing.timingType] = timing;
    });

    return grouped;
  }

  /**
   * 获取指定时机的 Popup Timing
   * @param timings PopupTiming数组
   * @param timingType 时机类型
   * @returns PopupTiming或undefined
   */
  getPopupTimingByType(
    timings: PopupTiming[],
    timingType: TimingType
  ): PopupTiming | undefined {
    return timings.find(timing => timing.timingType === timingType);
  }

  /**
   * 获取应该展示的弹窗队列
   * @param timing 弹窗时机配置
   * @param priorities 弹窗优先级配置
   * @param shownRecords 今日已展示记录（用于控制每日弹窗）
   * @returns 排序后的弹窗队列
   */
  getPopupQueue(
    timing: PopupTiming,
    priorities: PopupPriority[],
    shownRecords: Set<string> = new Set()
  ): PopupQueueItem[] {
    if (!timing.enabled) return [];

    const queue: PopupQueueItem[] = [];
    const ACTIVITY_QUALIFICATION_MAX = 2;

    // 收集活动弹窗
    timing.activityItems.forEach(item => {
      if (!item.enabled) return;

      // 检查每日弹窗限制
      if (item.popupDaily && shownRecords.has(`activity_${item.activityId}`)) {
        return;
      }

      queue.push({
        id: item.activityId,
        type: "activity",
        category: PopupCategory.ACTIVITY_TYPE,
        priority: item.popupPriority || 999,
        data: item,
      });
    });

    // 收集自定义弹窗
    timing.customItems.forEach(item => {
      if (!item.enabled) return;

      // 自定义弹窗暂不支持每日限制，可根据需要扩展
      queue.push({
        id: item.itemId,
        type: "custom",
        category: PopupCategory.CUSTOM_CONTENT,
        priority: 999, // 自定义弹窗默认优先级
        data: item,
      });
    });

    // 按优先级和sort排序
    queue.sort((a, b) => {
      // 先按优先级排序
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      // 优先级相同按sort排序
      return a.data.sort - b.data.sort;
    });

    // 对于 ACTIVITY_QUALIFICATION 类型，最多返回前2个
    if (timing.timingType === TimingType.ACTIVITY_QUALIFICATION) {
      const limitedByTiming = queue.slice(0, ACTIVITY_QUALIFICATION_MAX);
      return limitedByTiming;
    }

    // 应用优先级配置的条数限制
    const enabledPriorities = priorities.sort((a, b) => a.sort - b.sort);

    const limitedQueue: PopupQueueItem[] = [];
    const categoryCount: Record<string, number> = {};

    for (const item of queue) {
      const priority = enabledPriorities.find(
        p => p.category === item.category
      );
      if (!priority) continue;

      const currentCount = categoryCount[item.category] || 0;
      if (currentCount >= priority.maxPopupCount) continue;

      limitedQueue.push(item);
      categoryCount[item.category] = currentCount + 1;
    }

    return limitedQueue;
  }

  /**
   * 判断是否为活动类型的DisplayItem
   */
  isActivityDisplayItem(item: DisplayItem): boolean {
    return item.sourceType === SourceType.ACTIVITY;
  }

  /**
   * 获取DisplayItem的点击行为
   * @param item DisplayItem
   * @returns 点击行为描述
   */
  getDisplayItemClickAction(item: DisplayItem): {
    type: "activity" | "redirect" | "content";
    value: string;
    title?: string;
    content?: string;
    activityType?: ActivityType;
    groupId?: string;
  } {
    if (item.sourceType === SourceType.ACTIVITY) {
      return {
        type: "activity",
        value: item.activityId,
        activityType: item.activityType,
        groupId: item.groupId,
      };
    }

    if ("contentType" in item) {
      if (item.contentType === "REDIRECT_LINK") {
        return {
          type: "redirect",
          value: item.redirectUrl || item.internalRoute || "",
          activityType: item.activityType,
        };
      }

      if (item.contentType === "CUSTOM_CONTENT") {
        return {
          type: "content",
          value: item.title,
          title: item.title,
          content: item.content,
          activityType: item.activityType,
        };
      }
    }

    return {
      type: "redirect",
      value: "",
      activityType: undefined,
    };
  }

  /**
   * 获取今日已展示的弹窗记录键
   * @param date 日期字符串（默认今天）
   * @returns localStorage键
   */
  getTodayPopupRecordKey(date?: string): string {
    const today = date || new Date().toISOString().split("T")[0];
    return `popup_shown_${today}`;
  }

  /**
   * 获取余额不足弹窗的记录键
   * @param userId 用户ID
   * @param date 日期字符串（默认今天）
   * @returns localStorage键
   */
  getBalanceInsufficientRecordKey(userId: string, date?: string): string {
    const today = date || new Date().toISOString().split("T")[0];
    return `balance_insufficient_popup_${userId}_${today}`;
  }

  /**
   * 获取今日已展示的弹窗记录
   * @returns Set<string> 已展示的弹窗ID集合
   */
  getTodayShownPopups(): Set<string> {
    const key = this.getTodayPopupRecordKey();
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (error) {
      console.error("读取弹窗记录失败:", error);
    }
    return new Set();
  }

  /**
   * 记录今日已展示的弹窗
   * @param id 弹窗ID
   * @param type 弹窗类型（可选）
   */
  markPopupAsShown(id: string, type?: string): void {
    const key = this.getTodayPopupRecordKey();
    try {
      const shown = this.getTodayShownPopups();
      // 使用 type + id 作为唯一标识
      const popupKey = `${type ?? ""}_${id ?? ""}`;
      shown.add(popupKey);
      localStorage.setItem(key, JSON.stringify([...shown]));
    } catch (error) {
      console.error("保存弹窗记录失败:", error);
    }
  }

  /**
   * 获取余额不足弹窗的已展示记录
   * @param userId 用户ID
   * @returns 已展示的弹窗键列表
   */
  getBalanceInsufficientShownKeys(userId: string): string[] {
    const key = this.getBalanceInsufficientRecordKey(userId);
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * 记录余额不足弹窗为已展示
   * @param userId 用户ID
   * @param activityId 活动ID
   * @param activityType 活动类型
   * @returns 更新后的已展示记录列表
   */
  markBalanceInsufficientPopupAsShown(
    userId: string,
    activityId?: string,
    activityType?: string
  ): string[] {
    const key = this.getBalanceInsufficientRecordKey(userId);
    try {
      const shown = this.getBalanceInsufficientShownKeys(userId);
      // 使用 id + type 作为唯一标识
      const popupKey = `${activityId ?? ""}_${activityType ?? ""}`;
      if (!shown.includes(popupKey)) {
        shown.push(popupKey);
        localStorage.setItem(key, JSON.stringify(shown));
      }
      return shown;
    } catch (error) {
      console.error("保存余额不足弹窗记录失败:", error);
      return this.getBalanceInsufficientShownKeys(userId);
    }
  }

  /**
   * 清除过期的弹窗记录（保留最近7天）
   */
  cleanupOldPopupRecords(): void {
    const prefix = "popup_shown_";
    const today = new Date();
    const keepDays = 7;

    try {
      Object.keys(localStorage).forEach(key => {
        if (!key.startsWith(prefix)) return;

        const dateStr = key.replace(prefix, "");
        const recordDate = new Date(dateStr);
        const diffDays = Math.floor(
          (today.getTime() - recordDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (diffDays > keepDays) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("清理弹窗记录失败:", error);
    }
  }
}

// 创建单例实例
export const homeDisplayService = HomeDisplayService.getInstance();

// 导出便捷方法（绑定 this 上下文）
export const loadConfig =
  homeDisplayService.loadConfig.bind(homeDisplayService);
export const groupDisplayItemsByPosition =
  homeDisplayService.groupDisplayItemsByPosition.bind(homeDisplayService);
export const getDisplayItemsByPosition =
  homeDisplayService.getDisplayItemsByPosition.bind(homeDisplayService);
export const groupPopupTimingsByType =
  homeDisplayService.groupPopupTimingsByType.bind(homeDisplayService);
export const getPopupTimingByType =
  homeDisplayService.getPopupTimingByType.bind(homeDisplayService);
export const getPopupQueue =
  homeDisplayService.getPopupQueue.bind(homeDisplayService);
export const isActivityDisplayItem =
  homeDisplayService.isActivityDisplayItem.bind(homeDisplayService);
export const getDisplayItemClickAction =
  homeDisplayService.getDisplayItemClickAction.bind(homeDisplayService);
export const getTodayShownPopups =
  homeDisplayService.getTodayShownPopups.bind(homeDisplayService);
export const markPopupAsShown =
  homeDisplayService.markPopupAsShown.bind(homeDisplayService);
export const cleanupOldPopupRecords =
  homeDisplayService.cleanupOldPopupRecords.bind(homeDisplayService);
export const getBalanceInsufficientShownKeys =
  homeDisplayService.getBalanceInsufficientShownKeys.bind(homeDisplayService);
export const markBalanceInsufficientPopupAsShown =
  homeDisplayService.markBalanceInsufficientPopupAsShown.bind(
    homeDisplayService
  );
